import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Lock,
  Globe,
  Camera,
  X,
  ChevronRight,
  Crown,
  UserPlus
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Group type options
const groupTypes = [
  { value: 'general', label: 'General', icon: '⛳' },
  { value: 'club', label: 'Golf Club', icon: '🏌️' },
  { value: 'league', label: 'League', icon: '🏆' },
  { value: 'friends', label: 'Friends', icon: '👥' },
  { value: 'work', label: 'Work', icon: '💼' },
];

// Filter tabs
const filterTabs = [
  { id: 'my_groups', label: 'My Groups' },
  { id: 'discover', label: 'Discover' },
];

// Group Card Component
const GroupCard = ({ group, onView, onJoin, currentUserId }) => {
  const getTypeIcon = (type) => {
    const t = groupTypes.find(g => g.value === type);
    return t?.icon || '⛳';
  };

  return (
    <div 
      className="dark-card p-4 cursor-pointer hover:border-[var(--ps-border-light)] transition-colors"
      onClick={() => onView(group.group_id)}
      data-testid={`group-card-${group.group_id}`}
    >
      {/* Cover Image */}
      {group.cover_image && (
        <div className="relative -mx-4 -mt-4 mb-4 h-24 overflow-hidden rounded-t-xl">
          <img 
            src={group.cover_image} 
            alt={group.name} 
            className="w-full h-full object-cover"
          />
          {group.is_private && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Private
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-[var(--ps-primary-light)] flex items-center justify-center text-2xl flex-shrink-0">
          {getTypeIcon(group.group_type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-[var(--ps-text-primary)] truncate">{group.name}</h3>
            {!group.cover_image && group.is_private && (
              <Lock className="w-4 h-4 text-[var(--ps-text-muted)] flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-[var(--ps-text-muted)] mt-0.5">
            {group.member_count} member{group.member_count !== 1 ? 's' : ''}
            {group.post_count > 0 && ` • ${group.post_count} post${group.post_count !== 1 ? 's' : ''}`}
          </p>
          {group.description && (
            <p className="text-sm text-[var(--ps-text-secondary)] mt-2 line-clamp-2">
              {group.description}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
        {group.is_member ? (
          <Button
            size="sm"
            variant="ghost"
            className="flex-1 text-[var(--ps-text-secondary)]"
            onClick={() => onView(group.group_id)}
          >
            {group.user_role === 'admin' && <Crown className="w-4 h-4 mr-1 text-yellow-500" />}
            View Group
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : !group.is_private ? (
          <Button
            size="sm"
            className="flex-1 dark-btn-primary"
            onClick={() => onJoin(group.group_id)}
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Join Group
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="flex-1 text-[var(--ps-text-muted)]"
            disabled
          >
            <Lock className="w-4 h-4 mr-1" />
            Invite Only
          </Button>
        )}
      </div>
    </div>
  );
};

// Create Group Modal
const CreateGroupModal = ({ isOpen, onClose, onGroupCreated, user }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    group_type: 'general',
    is_private: false,
    cover_image: null
  });
  const [submitting, setSubmitting] = useState(false);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1200;
        
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setFormData(prev => ({ ...prev, cover_image: dataUrl }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/groups`, formData);
      toast.success("Group created!");
      onGroupCreated?.(response.data);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      group_type: 'general',
      is_private: false,
      cover_image: null
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Create Group</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Cover Image */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Cover Image (optional)</Label>
            {formData.cover_image ? (
              <div className="relative mt-2 h-24 rounded-xl overflow-hidden">
                <img src={formData.cover_image} alt="Cover" className="w-full h-full object-cover" />
                <button
                  onClick={() => setFormData(prev => ({ ...prev, cover_image: null }))}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="mt-2 flex items-center justify-center h-20 border-2 border-dashed border-[var(--ps-border)] rounded-xl cursor-pointer hover:border-[var(--ps-primary)] transition-colors">
                <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                <div className="flex items-center gap-2 text-[var(--ps-text-muted)]">
                  <Camera className="w-5 h-5" />
                  <span>Add cover photo</span>
                </div>
              </label>
            )}
          </div>

          {/* Name */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Group Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Weekend Golfers"
              className="dark-input mt-1"
            />
          </div>

          {/* Group Type */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Group Type</Label>
            <Select 
              value={formData.group_type} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, group_type: v }))}
            >
              <SelectTrigger className="dark-input mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
                {groupTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      <span>{type.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What's this group about?"
              className="dark-input mt-1 min-h-[80px] resize-none"
            />
          </div>

          {/* Private Toggle */}
          <div className="flex items-center justify-between p-3 bg-[var(--ps-bg-base)] rounded-xl">
            <div className="flex items-center gap-3">
              {formData.is_private ? (
                <Lock className="w-5 h-5 text-[var(--ps-primary)]" />
              ) : (
                <Globe className="w-5 h-5 text-[var(--ps-text-muted)]" />
              )}
              <div>
                <p className="font-medium text-sm">Private Group</p>
                <p className="text-xs text-[var(--ps-text-muted)]">
                  {formData.is_private ? 'Members must be invited' : 'Anyone can join'}
                </p>
              </div>
            </div>
            <Switch
              checked={formData.is_private}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_private: v }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} className="text-[var(--ps-text-secondary)]">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="dark-btn-primary">
            {submitting ? <div className="dark-spinner w-4 h-4" /> : 'Create Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Groups Page
export const GroupsPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('my_groups');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = { filter: activeFilter };
      if (searchQuery && activeFilter === 'discover') {
        params.search = searchQuery;
      }
      const response = await axios.get(`${API}/groups`, { params });
      setGroups(response.data);
    } catch (error) {
      console.error("Failed to fetch groups:", error);
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Join group
  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join`);
      toast.success("Joined group!");
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group");
    }
  };

  // View group
  const handleViewGroup = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  // Group created
  const handleGroupCreated = (newGroup) => {
    if (activeFilter === 'my_groups') {
      setGroups([newGroup, ...groups]);
    }
  };

  return (
    <div className="pb-4" data-testid="groups-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-[var(--ps-text-primary)]">Groups</h1>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
            data-testid="create-group-btn"
          >
            Create
          </button>
        </div>

        {/* Filter Tabs - Text with underline style */}
        <div className="flex items-center gap-4 mb-3">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`pb-2 text-lg font-semibold transition-colors border-b-2 ${
                activeFilter === tab.id
                  ? 'text-[var(--ps-primary)] border-[var(--ps-primary)]'
                  : 'text-[var(--ps-text-secondary)] border-transparent hover:text-[var(--ps-text-primary)]'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search (for Discover) */}
        {activeFilter === 'discover' && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              className="dark-input pl-10"
            />
          </div>
        )}
      </div>

      {/* Groups List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="dark-spinner" />
          </div>
        ) : groups.length === 0 ? (
          <div className="dark-empty-state">
            <Users className="w-12 h-12 mb-4" />
            <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
              {activeFilter === 'my_groups' ? 'No groups yet' : 'No groups found'}
            </h3>
            <p className="text-[var(--ps-text-secondary)] mb-4">
              {activeFilter === 'my_groups' 
                ? 'Create a group or discover existing ones!'
                : 'Try a different search or create your own group.'}
            </p>
            <div className="flex gap-2">
              <button 
                className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
                onClick={() => setShowCreateModal(true)}
              >
                Create Group
              </button>
              {activeFilter === 'my_groups' && (
                <Button 
                  variant="ghost"
                  className="text-[var(--ps-text-secondary)]"
                  onClick={() => setActiveFilter('discover')}
                >
                  Discover Groups
                </Button>
              )}
            </div>
          </div>
        ) : (
          groups.map((group) => (
            <GroupCard
              key={group.group_id}
              group={group}
              onView={handleViewGroup}
              onJoin={handleJoinGroup}
              currentUserId={user?.user_id}
            />
          ))
        )}
      </div>

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGroupCreated={handleGroupCreated}
        user={user}
      />
    </div>
  );
};

export default GroupsPage;
